import {
  NavLink,
  Navigate,
  Outlet,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import styles from './userDetails.module.scss';
import backIcon from 'images/back.png';
import {useEffect} from 'react';
import {useQuery} from '@apollo/client';
import {CHECK_IF_USER_IS_SIM_MODE} from 'graphql/queries/users';
import {useSimModeStore} from '../../../zustand/store';
import {Spin} from 'antd';
import {useSelector} from 'react-redux';
import {
  buildUserTabPath,
  getVisibleUserTabsForGrant,
  userVisibleTabs,
} from './userTabsConfig';
import {useHistoryContextStore} from '../../../zustand/historyContextStore';
import {useIsSuperAdmin} from 'hooks';

const UserDetails = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const activeAdminTab = useSelector(
    state => state?.auth?.userData?.user?.activeAdminTab,
  );

  const {isSimMode, setSimMode, setCanUseSimMode} = useSimModeStore(state => ({
    isSimMode: state.isSimMode,
    setSimMode: state.setSimMode,
    setCanUseSimMode: state.setCanUseSimMode,
  }));

  const {data: simModeData, loading: simModeLoading} = useQuery(
    CHECK_IF_USER_IS_SIM_MODE,
    {
      variables: {getUserByIdId: id},
    },
  );

  useEffect(() => {
    if (simModeData?.getUserById?.user) {
      const allowed = true; // simModeData.getUserById.user.toggledSimulationMode;
      setCanUseSimMode(allowed);
      if (!allowed && isSimMode) {
        setSimMode(false);
      }
    }
  }, [simModeData, isSimMode, setSimMode, setCanUseSimMode, id]);

  const visibleTabs = getVisibleUserTabsForGrant(activeAdminTab);

  const matchesPath = tab =>
    matchPath(
      {path: buildUserTabPath(id, tab.path), end: !!tab.end},
      location.pathname,
    );

  const activeTab = visibleTabs.find(matchesPath);

  // The user's name, used to label saved history entries.
  const simUser = simModeData?.getUserById?.user;
  const userName =
    [simUser?.firstName, simUser?.lastName].filter(Boolean).join(' ').trim() ||
    'User';
  const currentTabLabel = activeTab?.label || 'Profile Details';

  // Tell the global "Add to history" button how to label this user sub-tab
  // (with the user's name + tab), since the route carries an id the generic
  // route index can't resolve. Cleared when leaving the user page.
  const setPageDescriptor = useHistoryContextStore(
    state => state.setPageDescriptor,
  );
  const clearPageDescriptor = useHistoryContextStore(
    state => state.clearPageDescriptor,
  );
  const isSuperAdmin = useIsSuperAdmin();

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    setPageDescriptor({
      path: location.pathname,
      title: currentTabLabel,
      subtitle: userName,
      context: {
        scope: 'user',
        userId: id,
        userName,
        tabKey: activeTab?.key || 'profile-details',
        tabLabel: currentTabLabel,
      },
    });
    return () => clearPageDescriptor();
  }, [
    isSuperAdmin,
    location.pathname,
    userName,
    currentTabLabel,
    activeTab?.key,
    id,
    setPageDescriptor,
    clearPageDescriptor,
  ]);

  // If the current URL is a known user tab the admin can't access (e.g. landing
  // on /overview when only KYC is granted), send them to the first tab they do
  // have — before the ungated page mounts and fires a query that 403s.
  const matchedFullTab = userVisibleTabs.find(matchesPath);
  const matchedIsGranted =
    !matchedFullTab || visibleTabs.some(tab => tab.key === matchedFullTab.key);

  if (
    activeAdminTab &&
    matchedFullTab &&
    !matchedIsGranted &&
    visibleTabs.length
  ) {
    return <Navigate to={buildUserTabPath(id, visibleTabs[0].path)} replace />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerMeta}>
          <img
            onClick={() => navigate('/dashboard/users')}
            src={backIcon}
            alt="Go back"
          />
          <div>
            <span className={styles.breadcrumb}>User /</span>
            <h1>{activeTab?.label || 'User Profile'}</h1>
          </div>
        </div>
      </div>
      <section className={styles.section}>
        <nav className={styles.investorToggler}>
          {visibleTabs.map(tab => (
            <NavLink
              key={tab.key}
              end={tab.end}
              className={({isActive}) =>
                `${styles.navLink} ${
                  isActive ? styles.activeToggle : ''
                }`.trim()
              }
              to={buildUserTabPath(id, tab.path)}>
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </section>
      <section className={styles.content}>
        {simModeLoading ? (
          <div className={styles.loadingState}>
            <Spin size="large" />
          </div>
        ) : (
          <Outlet />
        )}
      </section>
    </div>
  );
};

export default UserDetails;
